import { create } from 'zustand';
import { TransactionData } from '@/types/types';
import type { CoverageInfo, CoverageState } from '@/types/payments';
import { getUserGymId } from '@/utils/gymAuth';
import { 
  createPayment, 
  updatePayment, 
  deletePayment,
  getPaymentsWithSubscription,
  getPaymentSummary,
  searchUserByName,
  PaymentWithSubscriptionRow,
} from '@/services/edgeFunctionWrappers/paymentsManagementWrapper';

type KPI = { unpaid: number; partial: number; paid: number; overpaid: number };

export interface SubscriptionPaymentSummary {
  subscription_id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  subscription_type: string;
  start_date: string;
  end_date: string;
  subscription_price: number;      // numeric
  subscription_status: string;     // active / expired ...
  payment_method: string | null;
  payment_frequency: string | null;
  is_paused: boolean | null;
  is_manually_activated: boolean | null;

  total_paid: number;              // numeric
  payments_count: number;
  last_payment_date: string | null;

  coverage_state: CoverageState | 'ignored';
  coverage_pct: number;
}

export interface TransactionsBySubscription {
  subscriptionId: string;
  userId: string;
  clientName: string;
  subscriptionPrice: number;
  items: TransactionData[];     // transactions liées (0..n)
  paymentsCount: number;        // items.length (completed uniquement ou tout ? -> ici tout)
  totalPaid: number;            // somme des completed
  coverageState: CoverageState; // unpaid / partial / paid / overpaid
  coveragePct: number;          // 0..100+ (overpay >100)
  // meta utiles pour l'UI
  subscriptionType?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

interface TransactionsState {
  // Transactions "riches" depuis v_payment_with_subscription formatées pour l'UI
  transactions: TransactionData[];

  // Résumés de couverture par abonnement (depuis la vue)
  subscriptionSummaries: SubscriptionPaymentSummary[];

  transactionsBySubscription: Record<string, TransactionsBySubscription>;

  // Dérivés prêts à l'emploi pour l'UI
  coverageBySub: Record<string, CoverageInfo>;
  kpis: KPI;

  isLoading: boolean;
  error: string | null;

  // Chargements
  fetchTransactions: () => Promise<void>;
  fetchSubscriptionSummaries: (onlyActive?: boolean) => Promise<void>;
  refreshPaymentsDashboard: () => Promise<void>;

  // Mutations
  addTransaction: (transaction: Omit<TransactionData, 'id'> & { userId?: string; updateSubscription?: boolean }) => Promise<boolean>;
  updateTransaction: (id: string, updates: Partial<TransactionData>) => Promise<boolean>;
  deleteTransaction: (id: string) => Promise<boolean>;

  getTransactionsForSubscription: (subscriptionId: string) => TransactionData[];
}

export const useTransactionsSupabaseStore = create<TransactionsState>((set, get) => ({
  transactions: [],
  subscriptionSummaries: [],
  coverageBySub: {},
  transactionsBySubscription: {},

  kpis: { unpaid: 0, partial: 0, paid: 0, overpaid: 0 },

  isLoading: false,
  error: null,

  /**
   * Charge les paiements enrichis via edge function,
   * puis mappe vers TransactionData (pour la table).
   */
  fetchTransactions: async () => {
    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      const { data, error } = await getPaymentsWithSubscription(gymId);

      if (error) throw error;

      const rows: PaymentWithSubscriptionRow[] = (data || []);

      // 1) Map à plat -> TransactionData
      const transactions: TransactionData[] = rows.map((row) => {
        const clientName = `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || 'Unknown Client';
        return {
          id: row.payment_id,
          client: clientName,
          amount: Number(row.payment_amount ?? 0),
          date: row.payment_date ? new Date(row.payment_date).toISOString().split('T')[0] : '',
          status: row.payment_status || 'pending',
          type: row.payment_description || 'Payment',
          paymentMethod: row.payment_method || 'cash',
          description: row.payment_description || 'Payment',
          userId: row.user_id,
          subscriptionId: row.subscription_id || undefined,
          // hint prix abo (facultatif)
          // @ts-ignore
          subscriptionPrice: row.subscription_price != null ? Number(row.subscription_price) : undefined,
          // @ts-ignore
          subscriptionType: row.subscription_type ?? undefined,
          // @ts-ignore
          startDate: row.subscription_start_date ?? undefined,
          // @ts-ignore
          endDate: row.subscription_end_date ?? undefined,
        };
      });

      // 2) Grouping par subscriptionId
      const bySub = new Map<string, TransactionsBySubscription>();

      for (const tx of transactions) {
        const sid = (tx as any).subscriptionId as string | undefined;

        // Skip les paiements non rattachés à un abonnement si tu veux, sinon rattache sous une clé spéciale
        if (!sid) continue;

        const key = sid;
        const existing = bySub.get(key);

        const price = Number((tx as any).subscriptionPrice ?? 0);
        const clientName = tx.client;
        const userId = tx.userId;

        if (!existing) {
          bySub.set(key, {
            subscriptionId: key,
            userId,
            clientName,
            subscriptionPrice: price,
            items: [tx],
            paymentsCount: 1,
            totalPaid: (tx.status?.toLowerCase() === 'completed') ? Number(tx.amount ?? 0) : 0,
            coverageState: 'unpaid', // sera recalculé ensuite
            coveragePct: 0,
            subscriptionType: (tx as any).subscriptionType,
            startDate: (tx as any).startDate,
            endDate: (tx as any).endDate,
          });
        } else {
          existing.items.push(tx);
          existing.paymentsCount += 1;
          if ((tx.status || '').toLowerCase() === 'completed') {
            existing.totalPaid += Number(tx.amount ?? 0);
          }
        }
      }

      // 3) Calcul coverage + KPI à partir du grouping
      const coverageBySub: Record<string, CoverageInfo> = {};
      const transactionsBySubscription: Record<string, TransactionsBySubscription> = {};
      const kpis: KPI = { unpaid: 0, partial: 0, paid: 0, overpaid: 0 };

      for (const [sid, bucket] of bySub.entries()) {
        const price = Number(bucket.subscriptionPrice ?? 0);
        const totalPaid = Number(bucket.totalPaid ?? 0);

        let state: CoverageState;
        if (price > 0 && totalPaid === 0) state = 'unpaid';
        else if (totalPaid > 0 && totalPaid < price) state = 'partial';
        else if (totalPaid === price) state = 'paid';
        else if (totalPaid > price) state = 'overpaid';
        else state = 'paid'; // price=0 && totalPaid=0 -> on ne compte pas dans KPI, mais on le met à "paid" neutre

        const coveragePct = price > 0 ? Math.round((totalPaid / price) * 100) : (totalPaid > 0 ? 100 : 0);

        // Maj du bucket
        bucket.coverageState = state;
        bucket.coveragePct = coveragePct;

        // KPI: ignorer price=0 && totalPaid=0
        const ignore = price === 0 && totalPaid === 0;
        if (!ignore) {
          (kpis as any)[state] += 1;
        }

        // coverage map
        coverageBySub[sid] = {
          subscriptionId: sid,
          price,
          totalPaid,
          paymentsCount: bucket.paymentsCount,
          isInstallment: bucket.paymentsCount > 1,
          state,
          coveragePct,
        };

        transactionsBySubscription[sid] = bucket;
      }

      set({
        transactions,
        transactionsBySubscription,
        coverageBySub,
        kpis,
      });
    } catch (err) {
      console.error('Error fetching transactions:', err);
      set({ error: 'Failed to fetch transactions' });
    } finally {
      set({ isLoading: false });
    }
  },


  /**
   * Charge la synthèse par abonnement via edge function,
   * calcule coverageBySub et KPIs (en ignorant price=0 && total_paid=0).
   */
  fetchSubscriptionSummaries: async (onlyActive = true) => {
    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      const { data, error } = await getPaymentSummary(gymId, onlyActive);
      
      if (error) throw error;

      const summaries: SubscriptionPaymentSummary[] = (data || []).map((r: any) => ({
        subscription_id: r.subscription_id,
        user_id: r.user_id,
        first_name: r.first_name,
        last_name: r.last_name,
        subscription_type: r.subscription_type,
        start_date: r.start_date,
        end_date: r.end_date,
        subscription_price: Number(r.subscription_price ?? 0),
        subscription_status: r.subscription_status,
        payment_method: r.payment_method,
        payment_frequency: r.payment_frequency,
        is_paused: r.is_paused,
        is_manually_activated: r.is_manually_activated,
        total_paid: Number(r.total_paid ?? 0),
        payments_count: Number(r.payments_count ?? 0),
        last_payment_date: r.last_payment_date,
        coverage_state: r.coverage_state,
        coverage_pct: Number(r.coverage_pct ?? 0),
      }));

      // Build coverageBySub & KPIs
      const coverageBySub: Record<string, CoverageInfo> = {};
      const kpis: KPI = { unpaid: 0, partial: 0, paid: 0, overpaid: 0 };

      for (const s of summaries) {
        const price = s.subscription_price;
        const totalPaid = s.total_paid;

        // ignorer price=0 && totalPaid=0 dans KPIs
        const ignore = price === 0 && totalPaid === 0;

        if (!ignore && (s.coverage_state === 'unpaid' || s.coverage_state === 'partial' || s.coverage_state === 'paid' || s.coverage_state === 'overpaid')) {
          (kpis as any)[s.coverage_state] += 1;
        }

        coverageBySub[s.subscription_id] = {
          subscriptionId: s.subscription_id,
          price,
          totalPaid,
          paymentsCount: s.payments_count,
          isInstallment: s.payments_count > 1,
          state: (s.coverage_state === 'ignored' ? 'paid' : s.coverage_state) as CoverageState, // fallback neutre
          coveragePct: s.coverage_pct,
        };
      }

      set({ subscriptionSummaries: summaries, coverageBySub, kpis });
    } catch (err) {
      console.error('Error fetching subscription summaries:', err);
      set({ error: 'Failed to fetch subscription summaries' });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Refresh combiné pour le dashboard paiements (transactions + synthèses).
   */
  refreshPaymentsDashboard: async () => {
    set({ isLoading: true, error: null });
    try {
      await Promise.all([
        get().fetchTransactions(),
        get().fetchSubscriptionSummaries(true),
      ]);
    } finally {
      set({ isLoading: false });
    }
  },

  // ---------------- Mutations paiements ----------------

  addTransaction: async (transaction) => {
    set({ isLoading: true, error: null });
    try {
      let userId = transaction.userId;
      const gymId = getUserGymId();

      // Si pas d'userId fourni, tentative de résolution par nom via edge function
      if (!userId && transaction.client) {
        const parts = transaction.client.split(' ');
        const first = parts[0];
        const last = parts.slice(1).join(' ');
        if (first && last) {
          const { data: foundUserId } = await searchUserByName(gymId, first, last);
          if (foundUserId) userId = foundUserId;
        }
      }

      if (!userId) {
        console.error('Failed to add transaction: No valid user_id found');
        set({ error: 'No valid user_id found' });
        return false;
      }

      const { error } = await createPayment({
        user_id: userId,
        amount: transaction.amount,
        payment_date: new Date(transaction.date).toISOString(),
        status: transaction.status,
        payment_method: transaction.paymentMethod,
        description: transaction.description,
        subscription_id: (transaction as any).subscriptionId ?? undefined,
        gym_id: gymId,
      });

      if (error) throw error;

      // Refresh dashboard (transactions + coverage)
      await get().refreshPaymentsDashboard();
      return true;
    } catch (err) {
      console.error('Failed to add transaction:', err);
      set({ error: 'Failed to add transaction' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  updateTransaction: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const updateData: any = {};
      if (updates.amount !== undefined) updateData.amount = updates.amount;
      if (updates.status) updateData.status = updates.status;
      if (updates.paymentMethod) updateData.payment_method = updates.paymentMethod;
      if (updates.description) updateData.description = updates.description;

      const { error } = await updatePayment(id, getUserGymId(), updateData);

      if (error) throw error;

      await get().refreshPaymentsDashboard();
      return true;
    } catch (err) {
      console.error('Failed to update transaction:', err);
      set({ error: 'Failed to update transaction' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteTransaction: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await deletePayment(id, getUserGymId());

      if (error) throw error;

      await get().refreshPaymentsDashboard();
      return true;
    } catch (err) {
      console.error('Failed to delete transaction:', err);
      set({ error: 'Failed to update transaction' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  getTransactionsForSubscription: (subscriptionId: string) => {
    const state = get();
    return state.transactionsBySubscription[subscriptionId]?.items ?? [];
  },
}));
